import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useFormFieldValue from '@gef-ui/form-core/hooks/state/useFormFieldValue';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';
import CirclePlus from '@gef-ui/material-icons/CirclePlus';

import { ComponentLoader, Dialog } from 'components';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import { useIgnoreErrorsWhen } from 'components/molecules/ComponentLoader/hooks';
import { MaxLengths } from 'constants/forms';
import fetch from 'features/common/fetch';
import { urls } from 'features/common/fetch/constants';
import { submitDocumentId } from 'features/views/taskCreatingPages/actions';
import { dialogs } from 'features/views/taskCreatingPages/constants';
import { FIELDS } from 'features/views/taskCreatingPages/form';
import { getDocumentIds } from 'features/views/taskCreatingPages/selectors';
import { removeDocumentId } from 'features/views/taskCreatingPages/slice';

export const DocumentAttachment = ({ formName }) => {
	const dispatch = useDispatch();
	const documentId = useFormFieldValue({ fieldName: FIELDS.documentId });
	const documentIds = useSelector(getDocumentIds);
	const isDisabled = isNilOrEmpty(documentId);

	const ignoreErrorsForDocumentUpload = useIgnoreErrorsWhen({
		fetchStorePath: urls.checkTaskAttachment,
		attributePath: ['metaData', 'parsedContent', 'status'],
		value: 404,
	});

	const handleAddDocument = useCallback(() => {
		dispatch(submitDocumentId(formName));
	}, [dispatch, formName]);

	const handleRemove = useCallback(
		(id) => () => {
			dispatch(removeDocumentId(id));
		},
		[dispatch],
	);

	return (
		<Paper sx={{ p: 3, borderRadius: 2 }}>
			<Dialog name={dialogs.DOCUMENT_NOT_FOUND_DIALOG} mui>
				{getLabelMessage('sellerRequest:documentNotFound')}
			</Dialog>
			<Stack spacing={2}>
				<SectionHeader text={getLabelMessage('taskCreatingPages:documentAttachment')} />
				<Grid container>
					<Grid size={12}>
						<Grid container spacing={3}>
							<Tooltip title={getLabelMessage('tooltips:uploadDocumentAttachment')}>
								<Grid size={4}>
									<ComponentLoader
										fetchStorePath={fetch.constants.urls.checkTaskAttachment}
										ignoreErrors={ignoreErrorsForDocumentUpload}
									>
										<TextField
											size="small"
											fieldName={FIELDS.documentId}
											label={getLabelMessage('taskCreatingPages:documentId')}
											slotProps={{
												htmlInput: {
													maxLength: MaxLengths.DOCUMENT_ID,
												},

												input: {
													endAdornment: (
														<IconButton
															sx={{ cursor: 'pointer' }}
															onClick={handleAddDocument}
															disabled={isDisabled}
														>
															<CirclePlus />
														</IconButton>
													),
												},
											}}
										/>
									</ComponentLoader>
								</Grid>
							</Tooltip>
							<Grid size={8}>
								{!isNilOrEmpty(documentIds) &&
									documentIds.map((id) => (
										<Chip
											sx={{ mr: 1 }}
											size="small"
											key={id}
											label={id}
											onDelete={handleRemove(id)}
										/>
									))}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Stack>
		</Paper>
	);
};

DocumentAttachment.propTypes = {
	formName: PropTypes.string,
};

export default DocumentAttachment;
